import React, { useCallback, useEffect, useState } from 'react'
import axios from 'axios'
import { Icon } from 'react-icons-kit'
import { Link, useNavigate } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { chevronLeft } from 'react-icons-kit/feather'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { api } from '../../../utils/api'
import Requests from '../../../utils/Requests/Index'
import SingleSelect from '../../../components/select/Single'

const Transaction = () => {
    const navigate = useNavigate()
    const { register, handleSubmit, control, watch, setValue, formState: { errors } } = useForm()
    const [header] = useState({
        headers: { Authorization: "Bearer " + localStorage.getItem('token') }
    })
    const [agentOptions, setAgentOptions] = useState([])
    const [isLoading, setLoading] = useState(false)

    const selectedAgent = watch('agent')

    const { mutate } = useMutation({
        mutationFn: async (data) => {
            const response = await axios.put(`${api}ghotok/withdrawrequest`, data, header)
            return response.data
        },
        onSuccess: (data) => {
            setLoading(false)
            toast.success(data.message || "Withdrawal request created successfully.")
            navigate('/dashboard/agents/withdraw');
        },
        onError: (error) => {
            setLoading(false)
            toast.error(error.response?.data?.message || "An error occurred.")
        }
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                const result = await Requests.Ghotok.Index(header)
                if (result) {
                    const options = result.map(item => ({
                        label: `${item.name} - ${item.email} - ${item.phone}`,
                        value: item._id,
                        currentBalance: item.wallet.currentBalance,
                        mobileNumberFor: item.mobileNumberFor,
                        accountNumber: item.mobileBankingNumber,
                    }))
                    setAgentOptions(options)
                }
            } catch (error) {
                toast.error("Failed to fetch agents.")
            }
        }
        fetchData()
    }, [header])

    useEffect(() => {
        if (selectedAgent) {
            setValue('currentBalance', selectedAgent.currentBalance)
            setValue('paymentMethod', selectedAgent.mobileNumberFor)
            setValue('accountNumber', selectedAgent.accountNumber)
        } else {
            setValue('currentBalance', '')
            setValue('paymentMethod', 'Bkash')
            setValue('accountNumber', '')
        }
    }, [selectedAgent, setValue])

    const onSubmit = (data) => {
        setLoading(true)
        const submissionData = {
            id: data.agent.value,
            accountNumber: data.accountNumber,
            paymentMethod: data.paymentMethod,
            amount: data.amount,
            comment: data.comment
        }
        mutate(submissionData)
    }

    return (
        <div className="store-income pb-4">
            <div className="container-fluid">
                <div className="row">
                    <div className="col-12 col-padding">
                        <div className="card border-0 shadow-sm">
                            <div className="card-header p-3 p-lg-4 bg-white">
                                <div className="d-flex">
                                    <div><h6 className="mb-0">Transaction</h6></div>
                                    <div className="ml-auto">
                                        <Link
                                            to="/dashboard/agents/withdraw"
                                            type="button"
                                            className="btn shadow-none rounded-circle"
                                        >
                                            <Icon icon={chevronLeft} size={22} />
                                        </Link>
                                    </div>
                                </div>
                            </div>
                            <div className="card-body p-4">
                                <form onSubmit={handleSubmit(onSubmit)}>
                                    <div className="row">

                                        {/* Agent */}
                                        <div className="col-12">
                                            <div className="form-group mb-4">
                                                {errors.agent ? <p className="text-danger">{errors.agent.message}</p> : <p>Agent</p>}
                                                <Controller
                                                    name="agent"
                                                    control={control}
                                                    rules={{ required: "Agent is required" }}
                                                    render={({ field }) => (
                                                        <SingleSelect
                                                            {...field}
                                                            placeholder="Select Agent"
                                                            options={agentOptions}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </div>

                                        {/*Current Amount */}
                                        <div className="col-12 col-lg-6">
                                            <div className="form-group mb-4">
                                                <p>Current Amount</p>
                                                <input
                                                    type="number"
                                                    placeholder="Current amount"
                                                    className="form-control shadow-none"
                                                    {...register("currentBalance")}
                                                    readOnly
                                                />
                                            </div>
                                        </div>

                                        {/*Transaction Amount */}
                                        <div className="col-12 col-lg-6">
                                            <div className="form-group mb-4">
                                                {errors.amount ? <p className="text-danger">{errors.amount.message}</p> : <p>Transaction Amount</p>}
                                                <input
                                                    type="number"
                                                    placeholder="Enter transaction amount"
                                                    className={`form-control shadow-none ${errors.amount ? 'error' : ''}`}
                                                    {...register("amount", {
                                                        required: "Amount is required",
                                                        valueAsNumber: true,
                                                        validate: value => value <= (selectedAgent?.currentBalance || 0) || "Amount cannot be greater than current balance.",
                                                        min: { value: 500, message: "Minimum withdrawal amount is 500." }
                                                    })}
                                                    disabled={!selectedAgent || selectedAgent.currentBalance < 500}
                                                />
                                                {selectedAgent && selectedAgent.currentBalance < 500 && <p className="text-danger">Current balance is less than 500, cannot withdraw.</p>}
                                            </div>
                                        </div>


                                        {/* Payment method */}
                                        <div className="col-12 col-lg-6">
                                            <div className="form-group mb-4">
                                                <p>Payment method</p>
                                                <select
                                                    className="form-control shadow-none"
                                                    {...register("paymentMethod")}
                                                >
                                                    <option value="Bkash">Bkash</option>
                                                    <option value="Nagad">Nagad</option>
                                                    <option value="Rocket">Rocket</option>
                                                </select>
                                            </div>
                                        </div>

                                        {/* Payment Account */}
                                        <div className="col-12 col-lg-6">
                                            <div className="form-group mb-4">
                                                {errors.accountNumber ? <p className="text-danger">{errors.accountNumber.message}</p> : <p>Payment Account</p>}
                                                <input
                                                    type="text"
                                                    className="form-control shadow-none"
                                                    placeholder="Enter details"
                                                    {...register("accountNumber", { required: "Payment account is required" })}
                                                />
                                            </div>
                                        </div>

                                    </div>

                                    {/* Comment */}
                                    <div className="form-group mb-4">
                                        {errors.comment ? <p className="text-danger">{errors.comment.message}</p> : <p>Comment</p>}
                                        <textarea
                                            rows={5}
                                            className="form-control shadow-none"
                                            placeholder="Write comment"
                                            {...register("comment")}
                                        />
                                    </div>

                                    <div className="text-end">
                                        <button type="submit" className="btn shadow-none" disabled={isLoading}>
                                            {isLoading ? 'Creating...' : 'Create'}
                                        </button>
                                    </div>

                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default Transaction;